import { PrivateAgentAgentIdSchema, SaleskoOAuthProviders, type SaleskoOAuthProvider } from "@salesko/contracts";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { IntakeRoute } from "../workspace/IntakeRoute";
import { WorkspaceErrorBoundary } from "../workspace/components/WorkspaceErrorBoundary";
import { registerServiceWorker } from "../lib/pwa";
import {
  isPrivateAgentWorkspaceFacet,
  isWorkspaceViewId,
  type PrivateAgentWorkspaceFacet,
  type WorkspaceViewId
} from "../workspace/navItems";

type AppWorkspaceSearch = {
  agent?: string;
  frame?: string;
  privateAgentConversationId?: string;
  privateAgentFacet?: PrivateAgentWorkspaceFacet;
  share?: string;
  signin?: SaleskoOAuthProvider;
  view?: WorkspaceViewId;
};

export const Route = createFileRoute("/app")({
  ssr: false,
  // `?frame=<frameRecordId>` restores a session frame; `?share=<token>` restores
  // a read-only signed public frame without granting list/search/write access.
  // `?signin=<provider>` starts auth only for a normal editable workspace.
  validateSearch: (search: Record<string, unknown>): AppWorkspaceSearch => {
    const share = typeof search.share === "string" && search.share.length > 0 ? search.share : undefined;
    const frame = typeof search.frame === "string" && search.frame.length > 0 ? search.frame : undefined;
    const view = typeof search.view === "string" && isWorkspaceViewId(search.view) ? search.view : undefined;
    const privateAgentFacet =
      typeof search.privateAgentFacet === "string" && isPrivateAgentWorkspaceFacet(search.privateAgentFacet)
        ? search.privateAgentFacet
        : undefined;
    const privateAgentConversationId =
      typeof search.privateAgentConversationId === "string" && search.privateAgentConversationId.length > 0
        ? search.privateAgentConversationId
        : undefined;
    const signin =
      typeof search.signin === "string" && isSaleskoOAuthProvider(search.signin) ? search.signin : undefined;
    return {
      ...(search.agent !== undefined && !share ? { agent: PrivateAgentAgentIdSchema.parse(search.agent) } : {}),
      ...(frame && !share ? { frame } : {}),
      ...(share ? { share } : {}),
      ...(signin ? { signin } : {}),
      ...(view && !share ? { view } : {}),
      ...(view === "privateAgent" && privateAgentFacet && !share ? { privateAgentFacet } : {}),
      ...(view === "privateAgent" && privateAgentConversationId && !share ? { privateAgentConversationId } : {})
    };
  },
  head: () => ({
    meta: [
      { title: "Salesko Workspace - Contacts Intelligence Graph" },
      {
        name: "description",
        content:
          "Explore the Salesko contacts intelligence workspace and turn raw relationship notes into a living relationship graph."
      },
      { name: "robots", content: "noindex, nofollow" }
    ],
    links: [{ rel: "canonical", href: "https://app.salesko.ai/app" }]
  }),
  component: AppWorkspaceRoute
});

function AppWorkspaceRoute() {
  const [queryClient] = useState(() => new QueryClient());

  // Register the PWA service worker (no-op in dev / unsupported browsers).
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* app-shell restores the symmetric 14px gutter + canvas gradient that the
          pre-TanStack root.tsx provided; it was dropped in the SSR migration,
          leaving the workspace flush to the top and 28px short at the bottom. */}
      <div className="app-shell">
        <WorkspaceErrorBoundary>
          <IntakeRoute />
        </WorkspaceErrorBoundary>
      </div>
      {/* Dev-only. Default bottom-right overlaps the agent composer's send button
          (z-index 100000); move it clear of the bottom action area. */}
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" /> : null}
    </QueryClientProvider>
  );
}

function isSaleskoOAuthProvider(value: string): value is SaleskoOAuthProvider {
  return (SaleskoOAuthProviders as readonly string[]).includes(value);
}
